import React, { useEffect, useState } from "react";
import { AppShell } from "../components/AppShell";
import { api, errMsg } from "../lib/api";
import { toast } from "sonner";
import {
  Eye, ExternalLink, ImagePlus, KeyRound, Newspaper, PenLine, Plus, RefreshCw,
  Send, Sparkles, Trash2, X,
} from "lucide-react";

const inputCls = "mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none";
const inputStyle = { background: "#FFFFFF", borderColor: "#D7DBE7", color: "#1A2033" };
const labelStyle = { color: "#5A6478", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 };

const EMPTY_BRAND = { name: "", tagline: "", description: "", category: "Fashion", logo_url: "", website: "", instagram: "", myntra_url: "", meesho_url: "", city: "", keywords: "" };

export default function AdminBrands() {
  const [brands, setBrands] = useState([]);
  const [cats, setCats] = useState([]);
  const [modal, setModal] = useState(null); // {mode: create|edit|creds|articles, brand}
  const [form, setForm] = useState(EMPTY_BRAND);
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [articles, setArticles] = useState([]);
  const [topic, setTopic] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/brands");
      setBrands(data.brands || []);
      setCats(data.categories || []);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  useEffect(() => { load(); }, []);

  const openArticles = async (brand) => {
    setModal({ mode: "articles", brand });
    setTopic("");
    try {
      const { data } = await api.get(`/admin/brands/${brand.id}/articles`);
      setArticles(data.articles || []);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveBrand = async () => {
    if (form.name.trim().length < 2) { toast.error("Brand name required"); return; }
    setBusy(true);
    try {
      if (modal.mode === "create") {
        await api.post("/admin/brands", form);
        toast.success("Brand created — public page is live");
      } else {
        await api.put(`/admin/brands/${modal.brand.id}`, form);
        toast.success("Brand updated");
      }
      setModal(null); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const saveCreds = async () => {
    if (creds.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/brands/${modal.brand.id}/credentials`, creds);
      toast.success(`Client login created — share /brand-login with ${creds.email}`);
      setModal(null); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const generateArticle = async () => {
    if (topic.trim().length < 3) { toast.error("Enter an article topic"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/brands/${modal.brand.id}/articles/generate`, { topic: topic.trim(), keywords: modal.brand.keywords || "" });
      toast.success("AI article drafted");
      setTopic("");
      openArticles(modal.brand);
    } catch (e) { toast.error(errMsg(e, "Generation failed")); } finally { setBusy(false); }
  };

  const setArticleStatus = async (id, status) => {
    try {
      await api.put(`/admin/brand-articles/${id}`, { status });
      toast.success(status === "published" ? "Published" : "Unpublished");
      openArticles(modal.brand);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const deleteArticle = async (id) => {
    try {
      await api.delete(`/admin/brand-articles/${id}`);
      toast.success("Article deleted");
      openArticles(modal.brand);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const uploadBrandImage = async (brandId, file, articleId = "") => {
    const fd = new FormData();
    fd.append("file", file);
    if (articleId) fd.append("article_id", articleId);
    const { data } = await api.post(`/admin/brands/${brandId}/upload-image`, fd);
    return data;
  };

  const onAdminLogoFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !modal?.brand) return;
    try {
      const { url } = await uploadBrandImage(modal.brand.id, f);
      setForm((p) => ({ ...p, logo_url: url }));
      toast.success("Logo uploaded — click Save Changes to apply");
    } catch (err) { toast.error(errMsg(err, "Upload failed")); }
  };

  const onAdminArticleImage = async (e, articleId) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      await uploadBrandImage(modal.brand.id, f, articleId);
      toast.success("Image added inside the article");
      openArticles(modal.brand);
    } catch (err) { toast.error(errMsg(err, "Upload failed")); }
  };

  const toggleStatus = async (b) => {
    try {
      await api.put(`/admin/brands/${b.id}`, { status: b.status === "active" ? "paused" : "active" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const deleteBrand = async (b) => {
    if (!window.confirm(`Delete ${b.name} and all its articles?`)) return;
    try {
      await api.delete(`/admin/brands/${b.id}`);
      toast.success("Brand deleted"); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <AppShell role="admin">
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: "#1A2033" }}>BrandsByIndia</h1>
            <p className="text-sm mt-1" style={{ color: "#5A6478" }}>
              Client brands with public SEO pages, AI articles &amp; portal logins. Directory: <a href="/brands" target="_blank" rel="noopener" style={{ color: "#4F46E5", fontWeight: 600 }}>/brands</a>
            </p>
          </div>
          <button
            data-testid="admin-add-brand-btn"
            onClick={() => { setForm(EMPTY_BRAND); setModal({ mode: "create" }); }}
            className="px-5 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 shadow-md"
            style={{ color: "#FFFFFF" }}
          >
            <Plus className="w-4 h-4" /> Add Brand
          </button>
        </div>

        <div className="rounded-2xl border overflow-hidden" style={{ background: "#FFFFFF", borderColor: "#E4E7F0" }}>
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: "#F6F7FB", color: "#5A6478" }} className="text-left text-xs uppercase tracking-wider">
                <th className="px-4 py-3">Brand</th>
                <th className="px-4 py-3">Client Login</th>
                <th className="px-4 py-3">Articles</th>
                <th className="px-4 py-3">Views</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {brands.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-12 text-center" style={{ color: "#8A93A8" }} data-testid="admin-brands-empty">
                  No brands yet — click "Add Brand" to create the first client brand page.
                </td></tr>
              )}
              {brands.map((b) => (
                <tr key={b.id} data-testid={`admin-brand-row-${b.slug}`} style={{ borderTop: "1px solid #EEF0F6" }}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {b.logo_url ? <img src={b.logo_url} alt="" className="w-8 h-8 rounded-lg object-cover border" style={{ borderColor: "#E4E7F0" }} />
                        : <div className="w-8 h-8 rounded-lg grid place-items-center text-xs font-bold bg-gradient-to-br from-indigo-500 to-fuchsia-500" style={{ color: "#FFF" }}>{b.name[0]}</div>}
                      <div>
                        <div className="font-semibold" style={{ color: "#1A2033" }}>{b.name}</div>
                        <a href={`/brands/${b.slug}`} target="_blank" rel="noopener" className="text-[11px] flex items-center gap-1" style={{ color: "#4F46E5" }}>
                          /brands/{b.slug} <ExternalLink className="w-2.5 h-2.5" />
                        </a>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3" style={{ color: b.client_email ? "#1A2033" : "#A0A8BC" }}>
                    {b.client_email || "— not set —"}
                  </td>
                  <td className="px-4 py-3" style={{ color: "#1A2033" }}>{b.published}/{b.articles} live</td>
                  <td className="px-4 py-3" style={{ color: "#1A2033" }}>{(b.views || 0) + (b.article_views || 0)}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggleStatus(b)} data-testid={`toggle-status-${b.slug}`}
                      className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
                      style={b.status === "active" ? { background: "#E7F8EF", color: "#0E9F5D" } : { background: "#FDF1E2", color: "#C06A0C" }}>
                      {b.status}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 justify-end">
                      <button title="Articles" data-testid={`articles-btn-${b.slug}`} onClick={() => openArticles(b)}
                        className="p-2 rounded-lg border" style={{ borderColor: "#E4E7F0", color: "#4F46E5" }}><Newspaper className="w-4 h-4" /></button>
                      <button title="Client credentials" data-testid={`creds-btn-${b.slug}`} onClick={() => { setCreds({ email: b.client_email || "", password: "" }); setModal({ mode: "creds", brand: b }); }}
                        className="p-2 rounded-lg border" style={{ borderColor: "#E4E7F0", color: "#B4650E" }}><KeyRound className="w-4 h-4" /></button>
                      <button title="Edit" data-testid={`edit-btn-${b.slug}`} onClick={() => { setForm({ ...EMPTY_BRAND, ...b }); setModal({ mode: "edit", brand: b }); }}
                        className="p-2 rounded-lg border" style={{ borderColor: "#E4E7F0", color: "#1A2033" }}><PenLine className="w-4 h-4" /></button>
                      <button title="Delete" data-testid={`delete-btn-${b.slug}`} onClick={() => deleteBrand(b)}
                        className="p-2 rounded-lg border" style={{ borderColor: "#F3D5D5", color: "#C33" }}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(16,20,35,0.55)" }} onClick={() => setModal(null)}>
          <div className="w-full max-w-2xl rounded-2xl p-6 max-h-[88vh] overflow-y-auto" style={{ background: "#FFFFFF" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold" style={{ color: "#1A2033" }}>
                {modal.mode === "create" && "Add Brand"}
                {modal.mode === "edit" && `Edit — ${modal.brand.name}`}
                {modal.mode === "creds" && `Client Login — ${modal.brand.name}`}
                {modal.mode === "articles" && `Articles — ${modal.brand.name}`}
              </h2>
              <button onClick={() => setModal(null)} data-testid="modal-close" style={{ color: "#5A6478" }}><X className="w-5 h-5" /></button>
            </div>

            {(modal.mode === "create" || modal.mode === "edit") && (
              <div className="space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><label style={labelStyle}>Brand Name *</label>
                    <input data-testid="brand-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} style={inputStyle} disabled={modal.mode === "edit"} /></div>
                  <div><label style={labelStyle}>Category</label>
                    <select data-testid="brand-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} style={inputStyle}>
                      {cats.map((c) => <option key={c}>{c}</option>)}
                    </select></div>
                  <div><label style={labelStyle}>Tagline</label>
                    <input data-testid="brand-form-tagline" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className={inputCls} style={inputStyle} /></div>
                  <div><label style={labelStyle}>City</label>
                    <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} style={inputStyle} /></div>
                  {modal.mode === "edit" && (
                    <div><label style={labelStyle}>Boxes (visibility level — 3+ = Featured)</label>
                      <input data-testid="brand-form-boxes" type="number" min={1} max={20} value={form.boxes || 1} onChange={(e) => setForm({ ...form, boxes: Math.max(1, parseInt(e.target.value || "1", 10)) })} className={inputCls} style={inputStyle} /></div>
                  )}
                  <div><label style={labelStyle}>Website URL</label>
                    <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className={inputCls} style={inputStyle} /></div>
                  <div><label style={labelStyle}>Instagram URL</label>
                    <input value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} className={inputCls} style={inputStyle} /></div>
                  <div><label style={labelStyle}>Myntra Store URL</label>
                    <input value={form.myntra_url} onChange={(e) => setForm({ ...form, myntra_url: e.target.value })} className={inputCls} style={inputStyle} /></div>
                  <div><label style={labelStyle}>Meesho Store URL</label>
                    <input value={form.meesho_url} onChange={(e) => setForm({ ...form, meesho_url: e.target.value })} className={inputCls} style={inputStyle} /></div>
                  <div className="sm:col-span-2 flex items-end gap-2">
                    <div className="flex-1"><label style={labelStyle}>Logo Image URL</label>
                      <input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} className={inputCls} style={inputStyle} /></div>
                    {modal.mode === "edit" && (
                      <label className="px-3 py-2 rounded-lg border text-xs cursor-pointer flex items-center gap-1.5 whitespace-nowrap" style={{ borderColor: "#D7DBE7", color: "#4F46E5" }} data-testid="admin-upload-logo">
                        <ImagePlus className="w-3.5 h-3.5" /> Upload Logo
                        <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onAdminLogoFile} />
                      </label>
                    )}
                  </div>
                  <div className="sm:col-span-2"><label style={labelStyle}>Target Keywords (comma separated)</label>
                    <input data-testid="brand-form-keywords" value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} className={inputCls} style={inputStyle} placeholder="cotton kurtis, jaipuri prints, ethnic wear" /></div>
                  <div className="sm:col-span-2"><label style={labelStyle}>About the Brand</label>
                    <textarea data-testid="brand-form-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} style={inputStyle} /></div>
                </div>
                <button data-testid="brand-form-save" onClick={saveBrand} disabled={busy}
                  className="w-full py-3 rounded-xl text-sm font-semibold bg-gradient-to-r from-indigo-600 to-purple-600 disabled:opacity-60" style={{ color: "#FFFFFF" }}>
                  {busy ? "Saving…" : modal.mode === "create" ? "Create Brand & Public Page" : "Save Changes"}
                </button>
              </div>
            )}

            {modal.mode === "creds" && (
              <div className="space-y-3">
                <p className="text-sm" style={{ color: "#5A6478" }}>
                  The client logs in at <b style={{ color: "#4F46E5" }}>/brand-login</b> to see views, manage their profile and publish AI articles.
                </p>
                <div><label style={labelStyle}>Client Email</label>
                  <input data-testid="creds-email" type="email" value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })} className={inputCls} style={inputStyle} /></div>
                <div><label style={labelStyle}>Password (min 8 chars)</label>
                  <input data-testid="creds-password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} className={inputCls} style={inputStyle} placeholder="Set a new password" /></div>
                <button data-testid="creds-save" onClick={saveCreds} disabled={busy}
                  className="w-full py-3 rounded-xl text-sm font-semibold bg-gradient-to-r from-indigo-600 to-purple-600 disabled:opacity-60" style={{ color: "#FFFFFF" }}>
                  {busy ? "Saving…" : "Create / Reset Login"}
                </button>
              </div>
            )}

            {modal.mode === "articles" && (
              <div className="space-y-4">
                <div className="rounded-xl p-4 border" style={{ background: "#F6F5FF", borderColor: "#DDD9F8" }}>
                  <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: "#4F46E5" }}>
                    <Sparkles className="w-4 h-4" /> Generate AI SEO Article
                  </div>
                  <div className="flex gap-2 mt-3 flex-col sm:flex-row">
                    <input data-testid="admin-article-topic" value={topic} onChange={(e) => setTopic(e.target.value)}
                      placeholder={`e.g. Best ${modal.brand.category?.toLowerCase() || "products"} from ${modal.brand.name} in 2026`}
                      className="flex-1 border rounded-lg px-3 py-2.5 text-sm focus:outline-none" style={inputStyle} />
                    <button data-testid="admin-generate-article" onClick={generateArticle} disabled={busy}
                      className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-gradient-to-r from-indigo-600 to-purple-600 disabled:opacity-60 flex items-center justify-center gap-2 whitespace-nowrap" style={{ color: "#FFFFFF" }}>
                      {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />}
                      {busy ? "Writing… ~60s" : "Generate"}
                    </button>
                  </div>
                </div>

                {articles.length === 0 ? (
                  <div className="text-sm text-center py-8" style={{ color: "#8A93A8" }} data-testid="admin-no-articles">No articles yet.</div>
                ) : (
                  <div className="space-y-2">
                    {articles.map((a) => (
                      <div key={a.id} className="border rounded-xl p-3.5 flex flex-col sm:flex-row sm:items-center gap-3" style={{ borderColor: "#E4E7F0" }} data-testid={`admin-article-${a.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold truncate" style={{ color: "#1A2033" }}>{a.title}</div>
                          <div className="text-[11px] mt-1 flex items-center gap-3" style={{ color: "#8A93A8" }}>
                            <span className="px-2 py-0.5 rounded-full font-bold uppercase tracking-wider"
                              style={a.status === "published" ? { background: "#E7F8EF", color: "#0E9F5D" } : { background: "#FDF1E2", color: "#C06A0C" }}>
                              {a.status}
                            </span>
                            <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{a.views || 0}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <label className="text-xs px-3 py-1.5 rounded-full border cursor-pointer flex items-center gap-1" style={{ borderColor: "#E4E7F0", color: "#4F46E5" }} data-testid={`admin-add-image-${a.id}`}>
                            <ImagePlus className="w-3 h-3" /> Image
                            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => onAdminArticleImage(e, a.id)} />
                          </label>
                          {a.status === "published" ? (
                            <>
                              <a href={`/brands/${modal.brand.slug}/${a.slug}`} target="_blank" rel="noopener"
                                className="text-xs px-3 py-1.5 rounded-full border" style={{ borderColor: "#E4E7F0", color: "#4F46E5" }}>View</a>
                              <button onClick={() => setArticleStatus(a.id, "draft")} className="text-xs px-3 py-1.5 rounded-full border" style={{ borderColor: "#E4E7F0", color: "#5A6478" }}>Unpublish</button>
                            </>
                          ) : (
                            <button data-testid={`admin-publish-${a.id}`} onClick={() => setArticleStatus(a.id, "published")}
                              className="text-xs px-3.5 py-1.5 rounded-full font-semibold flex items-center gap-1.5" style={{ background: "#0E9F5D", color: "#FFFFFF" }}>
                              <Send className="w-3 h-3" /> Publish
                            </button>
                          )}
                          <button onClick={() => deleteArticle(a.id)} className="p-1.5 rounded-lg border" style={{ borderColor: "#F3D5D5", color: "#C33" }}><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </AppShell>
  );
}
